import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, Pause, Play, X } from 'lucide-react';
import { useEditorStore } from '../store/editor';
import { CellSvg } from '../render/CellSvg';

export const Presentation: React.FC = () => {
  const project = useEditorStore((state) => state.project);
  const open = useEditorStore((state) => state.presentationOpen);
  const setOpen = useEditorStore((state) => state.setPresentationOpen);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (open) {
      setIndex(0);
      setPlaying(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !playing || !project) return undefined;
    const cell = project.cells[index];
    const timer = window.setTimeout(() => {
      if (index >= project.cells.length - 1) {
        setPlaying(false);
      } else {
        setIndex((value) => value + 1);
      }
    }, Math.max(250, cell.durationMs));
    return () => window.clearTimeout(timer);
  }, [index, open, playing, project]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
      if (event.key === 'ArrowRight' && project) setIndex((value) => Math.min(project.cells.length - 1, value + 1));
      if (event.key === 'ArrowLeft') setIndex((value) => Math.max(0, value - 1));
      if (event.key === ' ') {
        event.preventDefault();
        setPlaying((value) => !value);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, project, setOpen]);

  useEffect(() => {
    if (!open || !project) return;
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    if (project.cells[index].audioDataUrl) void audio.play().catch(() => undefined);
  }, [index, open, project]);

  if (!open || !project) return null;
  const cell = project.cells[index];

  return (
    <div className="presentation-overlay" ref={rootRef} role="dialog" aria-modal="true" aria-label={`Presenting ${project.title}`}>
      <header className="presentation-header">
        <div><strong>{project.title}</strong><span>{index + 1} / {project.cells.length}</span></div>
        <div className="presentation-actions">
          <button type="button" className="presentation-button" onClick={() => setPlaying((value) => !value)}>{playing ? <Pause size={18} /> : <Play size={18} />} {playing ? 'Pause' : 'Play'}</button>
          <button type="button" className="icon-button light" onClick={() => void rootRef.current?.requestFullscreen?.()} aria-label="Enter fullscreen"><Maximize2 size={19} /></button>
          <button type="button" className="icon-button light" onClick={() => setOpen(false)} aria-label="Close presentation"><X size={21} /></button>
        </div>
      </header>
      <main className="presentation-stage">
        <button type="button" className="presentation-nav previous" onClick={() => setIndex((value) => Math.max(0, value - 1))} disabled={index === 0} aria-label="Previous scene"><ChevronLeft size={32} /></button>
        <div className="presentation-cell-wrap">
          <CellSvg project={project} cell={cell} preserveAspectRatio="xMidYMid meet" />
          <div className="presentation-caption"><h1>{cell.title}</h1>{cell.description && <p>{cell.description}</p>}</div>
        </div>
        <button type="button" className="presentation-nav next" onClick={() => setIndex((value) => Math.min(project.cells.length - 1, value + 1))} disabled={index === project.cells.length - 1} aria-label="Next scene"><ChevronRight size={32} /></button>
      </main>
      <footer className="presentation-timeline">
        {project.cells.map((item, cellIndex) => (
          <button key={item.id} type="button" className={cellIndex === index ? 'is-active' : ''} onClick={() => setIndex(cellIndex)} aria-label={`Show ${item.title}`}><span style={{ animationDuration: `${item.durationMs}ms` }} /></button>
        ))}
      </footer>
      <audio ref={audioRef} src={cell.audioDataUrl} />
    </div>
  );
};
